import React, { type FC, useCallback, useEffect, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import AiGenerativeAudioBriefingIcon from '@atlaskit/icon-lab/core/ai-generative-audio-briefing';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Tooltip from '@atlaskit/tooltip';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import type { ActiveContent } from '../../../types/activeContent';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import { useAudioShareLink } from '../../hooks/useAudioShareLink';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';

const i18n = defineMessages({
	listen: {
		id: 'audio.audio-player.audio-listen-button.listen',
		defaultMessage: 'Listen',
		description: 'Label and tooltip for the audio listen button',
	},
	browserNotSupported: {
		id: 'audio.audio-player.audio-listen-button.browserNotSupported',
		defaultMessage: 'Your browser doesn’t support this feature',
		description:
			'Message in tooltip which tells user that their browser does not support Audio features',
	},
});

type Source = 'contentWrapper';

type AudioListenButtonProps = {
	isDisabled?: boolean;
	isAutoOpenDisabled?: boolean;
	shape?: 'default' | 'circle';
	activeContent: ActiveContent;
	source: Source;
};

export const AudioListenButton: FC<AudioListenButtonProps> = ({
	shape = 'default',
	isDisabled,
	isAutoOpenDisabled = false,
	activeContent,
	source,
}) => {
	// Assume audio is enabled for SSR until client side useEffect checks browser support.
	// This is to avoid expensive hydration issues since in most cases audio will indeed be supported.
	const [audioSupportedByBrowser, setAudioSupportedByBrowser] = useState<boolean>(true);
	const { formatMessage, locale } = useIntl();
	const { sendUIEvent, sendScreenEvent } = usePlatformInstrumentation();

	useEffect(() => {
		// Skip browser capability check during SSR since MediaSource doesn't exist on the server.
		// Note: Confluence uses a special SSR implementation where useEffect runs on the server.
		// therefore this extra defensive check is needed
		// More info can be found here: https://hello.atlassian.net/wiki/spaces/CCPERF/blog/2025/01/04/4610696218/SSR+ing+Confluence+Editor+via+Full+Cycle+Rendering
		if (process.env.REACT_SSR) {
			return;
		}

		const isAudioSupportedByBrowser =
			typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');
		if (!isAudioSupportedByBrowser) {
			setAudioSupportedByBrowser(false);
			sendScreenEvent({
				eventType: 'screen',
				name: 'audioButtonDisabled',
				attributes: {
					source,
					locale,
					version: 'v3'
				},
			});
		} else {
			sendScreenEvent({
				eventType: 'screen',
				name: 'audioButton',
				attributes: {
					source,
					locale,
					version: 'v3'
				},
			});
		}
	}, [sendScreenEvent, locale, source]);

	const [isHovered, setIsHovered] = useState(false);
	const { openAudioPlayer, isOpen } = useAudioPlayerStore();
	const buttonRef = React.useRef<HTMLButtonElement>(null);

	const { state: streamingState } = useAudioPlayerStreamingStore();
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;
	useAudioShareLink(activeContent, isUninitialized, isAutoOpenDisabled);

	const sendAnalyticsUIEvent = useCallback(
		(action: string) => {
			sendUIEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'audioPopup',
				source: 'contentWrapper',
				attributes: {
					action,
					contentType: activeContent.contentType ?? 'page',
					// TODO: Determine if pageMode is needed here as it was included within v2
					// analytics but requires a Confluence dependency
				},
			});
		},
		[sendUIEvent, activeContent],
	);

	const tooltipContent = audioSupportedByBrowser
		? formatMessage(i18n.listen)
		: formatMessage(i18n.browserNotSupported);

	const handleClick = useCallback(() => {
		if (!isOpen) {
			sendAnalyticsUIEvent('openPopup');
		}

		openAudioPlayer(activeContent, { isUninitialized, returnFocusRef: buttonRef });
	}, [isOpen, sendAnalyticsUIEvent, openAudioPlayer, activeContent, isUninitialized]);

	return (
		<Tooltip content={tooltipContent} position="left">
			<IconButton
				ref={buttonRef}
				label={formatMessage(i18n.listen)}
				appearance="subtle"
				icon={isHovered ? LinkExternalIcon : AiGenerativeAudioBriefingIcon}
				shape={shape}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onClick={handleClick}
				testId="audio-listen-button"
				isDisabled={isDisabled || !audioSupportedByBrowser}
			/>
		</Tooltip>
	);
};
